import React from 'react';

interface LogoProps {
    /**
     * Size of the logo in pixels (height)
     * Width scales proportionally
     * @default 40
     */
    size?: number;

    /**
     * Color variant
     * @default 'dark'
     */
    variant?: 'dark' | 'light';

    /**
     * Additional CSS classes
     */
    className?: string;

    /**
     * Custom primary color (overrides default purple)
     * @default '#8b5cf6'
     */
    primaryColor?: string;

    /**
     * Custom accent color
     * @default '#bdb0f4'
     */
    accentColor?: string;
}

export const Logo: React.FC<LogoProps> = ({
                                              size = 40,
                                              variant = 'dark',
                                              className = '',
                                              primaryColor = '#8b5cf6',
                                              accentColor = '#bdb0f4'
                                          }) => {
    // SVG has viewBox="0 0 154.41 59.76", so aspect ratio is ~2.58
    const width = size * 2.58;

    return (
        <svg
            width={width}
            height={size}
            viewBox="0 0 154.41 59.76"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <defs>
                <style>{`
                    .st0, .st1, .st2 {
                        stroke: ${primaryColor};
                    }
                    .st0, .st1, .st2, .st3 {
                        stroke-miterlimit: 10;
                    }
                    .st0, .st2 {
                        fill: none;
                    }
                    .st1 {
                        fill: ${primaryColor};
                    }
                    .st2 {
                        stroke-width: 5px;
                    }
                    .st3 {
                        fill: ${accentColor};
                        stroke: ${accentColor};
                    }
                `}</style>
            </defs>
            <g>
                <path className="st1" d="M10.58,39.97c-2.02,0-3.77-.3-5.27-.91s-2.66-1.53-3.5-2.76-1.27-2.79-1.3-4.65h5.51c.05.78.27,1.44.64,1.97.37.53.89.93,1.54,1.2s1.42.4,2.3.4c.79,0,1.47-.11,2.05-.33s1.02-.52,1.33-.91c.31-.39.46-.85.46-1.37,0-.47-.14-.86-.43-1.19-.29-.33-.72-.61-1.29-.86s-1.31-.47-2.2-.67l-2.57-.6c-2.09-.48-3.74-1.26-4.93-2.34s-1.79-2.54-1.79-4.36c0-1.5.41-2.81,1.22-3.94s1.93-2,3.34-2.63c1.42-.62,3.04-.93,4.87-.93s3.5.32,4.88.95c1.38.63,2.45,1.52,3.2,2.65.76,1.13,1.15,2.45,1.17,3.94h-5.51c-.08-.88-.43-1.56-1.07-2.05s-1.53-.73-2.68-.73c-.76,0-1.4.1-1.92.3-.52.2-.91.48-1.17.83s-.39.76-.39,1.21c0,.5.15.92.44,1.26.29.34.71.63,1.25.85s1.17.42,1.89.59l2.1.49c1.17.25,2.21.59,3.13,1.02.92.43,1.69.94,2.33,1.54.63.6,1.12,1.28,1.45,2.07s.5,1.65.5,2.62c0,1.53-.38,2.84-1.15,3.94-.76,1.1-1.86,1.94-3.29,2.53s-3.14.88-5.15.88Z"/>
                <path className="st1" d="M64.87,39.97c-2.18,0-4.13-.48-5.86-1.45-1.73-.97-3.1-2.37-4.1-4.23-1-1.85-1.5-4.1-1.5-6.75s.51-4.93,1.52-6.78,2.39-3.26,4.12-4.23,3.68-1.45,5.82-1.45c1.44,0,2.78.2,4,.59s2.31.97,3.25,1.73,1.71,1.69,2.3,2.79c.59,1.1.97,2.36,1.15,3.76h-5.81c-.1-.6-.28-1.13-.54-1.6-.26-.47-.59-.88-1-1.21-.41-.34-.88-.59-1.42-.76s-1.14-.26-1.79-.26c-1.18,0-2.2.29-3.07.88-.86.59-1.52,1.43-1.98,2.53-.46,1.1-.69,2.44-.69,4.01s.24,2.97.71,4.07c.47,1.1,1.13,1.93,1.98,2.48.85.55,1.86.83,3.02.83.65,0,1.25-.09,1.79-.26s1.02-.43,1.43-.76.75-.74,1.02-1.21c.27-.47.46-1.01.55-1.6h5.81c-.11,1.08-.42,2.15-.93,3.2s-1.22,2-2.12,2.86c-.91.86-2,1.55-3.28,2.06s-2.74.76-4.39.76Z"/>
                <path className="st1" d="M82.12,39.64v-18.18h2.15l.02,4.15h-.24c.25-1.02.65-1.85,1.2-2.5s1.2-1.14,1.94-1.46c.74-.33,1.5-.49,2.28-.49,1.33,0,2.43.41,3.29,1.24.86.82,1.41,1.93,1.65,3.32h-.36c.22-.94.61-1.75,1.17-2.43.56-.68,1.25-1.2,2.07-1.57s1.7-.55,2.67-.55c1.08,0,2.05.24,2.9.71.85.47,1.52,1.17,2.02,2.1.49.93.74,2.07.74,3.44v12.25h-2.2v-12.18c0-1.46-.39-2.54-1.17-3.24s-1.74-1.04-2.88-1.04c-.87,0-1.63.18-2.29.55s-1.18.89-1.55,1.56c-.37.67-.56,1.46-.56,2.36v11.99h-2.2v-12.38c0-1.26-.37-2.25-1.1-2.98-.73-.73-1.68-1.1-2.84-1.1-.81,0-1.56.19-2.24.57s-1.23.92-1.64,1.63-.62,1.56-.62,2.57v11.69h-2.21Z"/>
                <path className="st1" d="M115.97,40.05c-1.11,0-2.11-.21-3.02-.63s-1.63-1.05-2.16-1.87c-.54-.82-.81-1.82-.81-2.99,0-.9.17-1.66.5-2.27.34-.61.82-1.12,1.45-1.51s1.37-.71,2.22-.94,1.79-.41,2.82-.54c1.01-.13,1.86-.24,2.57-.33.7-.09,1.24-.24,1.62-.45.37-.21.56-.54.56-.99v-.42c0-.81-.17-1.51-.5-2.1-.33-.59-.81-1.04-1.45-1.35-.63-.31-1.41-.47-2.32-.47-.86,0-1.61.13-2.26.4s-1.19.61-1.62,1.04c-.43.43-.75.9-.97,1.41l-2.11-.72c.4-.99.97-1.78,1.69-2.37s1.55-1.03,2.46-1.31c.91-.28,1.83-.42,2.76-.42.72,0,1.45.1,2.21.28s1.46.51,2.11.96c.65.45,1.18,1.07,1.58,1.85.4.79.6,1.78.6,2.98v12.34h-2.16v-2.88h-.16c-.26.55-.65,1.08-1.15,1.59-.51.5-1.14.91-1.88,1.23-.74.31-1.61.47-2.59.47ZM116.29,38.08c1.11,0,2.07-.24,2.88-.73s1.44-1.14,1.89-1.97.67-1.73.67-2.72v-2.59c-.16.15-.43.29-.8.41s-.79.22-1.26.31c-.47.09-.95.16-1.42.23s-.89.12-1.25.16c-1.01.12-1.87.31-2.59.59-.72.27-1.27.64-1.64,1.11-.37.47-.56,1.06-.56,1.77s.18,1.34.54,1.85.85.9,1.46,1.17,1.32.41,2.1.41Z"/>
                <path className="st1" d="M135.63,21.46v1.92h-8.65v-1.92h8.65ZM129.68,17.14h2.21v18.22c0,.9.2,1.56.6,1.98s1.01.59,1.84.5c.15,0,.34-.02.56-.06.22-.04.43-.08.63-.12l.46,1.9c-.24.08-.5.14-.79.18s-.58.07-.87.08c-1.45.1-2.59-.25-3.41-1.03-.82-.79-1.23-1.88-1.23-3.29v-18.36Z"/>
                <path className="st1" d="M146.51,40.04c-1.7,0-3.17-.4-4.42-1.2-1.24-.8-2.2-1.9-2.88-3.31s-1.02-3.03-1.02-4.86.33-3.46,1-4.88c.67-1.42,1.6-2.54,2.8-3.35,1.2-.81,2.59-1.22,4.17-1.22,1,0,1.96.18,2.88.55s1.75.92,2.49,1.66,1.32,1.68,1.74,2.82.63,2.48.63,4.02v.96h-14.26v-1.9h13.08l-1.01.72c0-1.3-.22-2.47-.67-3.5s-1.08-1.84-1.9-2.44-1.82-.89-2.98-.89-2.15.3-3.02.91c-.86.61-1.53,1.41-2.02,2.41s-.72,2.09-.72,3.29v1.14c0,1.42.25,2.66.75,3.73s1.21,1.9,2.12,2.49c.92.59,2,.89,3.25.89.85,0,1.59-.14,2.24-.41.64-.27,1.19-.63,1.63-1.08.44-.45.77-.94.98-1.47l2.1.67c-.28.76-.73,1.46-1.36,2.11-.62.65-1.41,1.17-2.35,1.57-.94.4-2.03.59-3.25.59Z"/>
            </g>
            <circle className="st1" cx="15.53" cy="4.41" r="3.91"/>
            <circle className="st1" cx="52.04" cy="45.75" r="3.41"/>
            <circle className="st0" cx="51.05" cy="7.85" r="4.44"/>
            <circle className="st0" cx="78.28" cy="54.29" r="4.98"/>
            <line className="st1" x1="18.15" y1="52.46" x2="31.51" y2="38.55"/>
            <line className="st1" x1="18.68" y1="6.7" x2="31.25" y2="19.01"/>
            <line className="st1" x1="47.88" y1="10.77" x2="42.96" y2="17.39"/>
            <line className="st1" x1="55.13" y1="6.7" x2="76.56" y2="4.41"/>
            <line className="st1" x1="78.17" y1="8.11" x2="72.11" y2="18.03"/>
            <line className="st1" x1="76.56" y1="49.31" x2="72.56" y2="36.74"/>
            <line className="st1" x1="73.3" y1="54.49" x2="54.87" y2="47.64"/>
            <line className="st1" x1="49.49" y1="43.49" x2="42.96" y2="36.74"/>
            <circle className="st3" cx="15.53" cy="55.36" r="3.91"/>
            <circle className="st2" cx="36.82" cy="28.49" r="10.66"/>
            <circle className="st3" cx="80.47" cy="4.41" r="3.91"/>
        </svg>
    );
};

// Export icon-only version (just the network graphic, no text)
export const LogoIcon: React.FC<Omit<LogoProps, 'variant'>> = ({
                                                                   size = 40,
                                                                   className = '',
                                                                   primaryColor = '#8b5cf6',
                                                                   accentColor = '#bdb0f4'
                                                               }) => {
    return (
        <svg
            width={size}
            height={size}
            viewBox="10 0 75 60"
            xmlns="http://www.w3.org/2000/svg"
            className={className}
        >
            <defs>
                <style>{`
                    .st0, .st1, .st2 {
                        stroke: ${primaryColor};
                    }
                    .st0, .st1, .st2, .st3 {
                        stroke-miterlimit: 10;
                    }
                    .st0, .st2 {
                        fill: none;
                    }
                    .st1 {
                        fill: ${primaryColor};
                    }
                    .st2 {
                        stroke-width: 5px;
                    }
                    .st3 {
                        fill: ${accentColor};
                        stroke: ${accentColor};
                    }
                `}</style>
            </defs>

            <circle className="st1" cx="15.53" cy="4.41" r="3.91"/>
            <circle className="st1" cx="52.04" cy="45.75" r="3.41"/>
            <circle className="st0" cx="51.05" cy="7.85" r="4.44"/>
            <circle className="st0" cx="78.28" cy="54.29" r="4.98"/>
            <line className="st1" x1="18.15" y1="52.46" x2="31.51" y2="38.55"/>
            <line className="st1" x1="18.68" y1="6.7" x2="31.25" y2="19.01"/>
            <line className="st1" x1="47.88" y1="10.77" x2="42.96" y2="17.39"/>
            <line className="st1" x1="55.13" y1="6.7" x2="76.56" y2="4.41"/>
            <line className="st1" x1="78.17" y1="8.11" x2="72.11" y2="18.03"/>
            <line className="st1" x1="76.56" y1="49.31" x2="72.56" y2="36.74"/>
            <line className="st1" x1="73.3" y1="54.49" x2="54.87" y2="47.64"/>
            <line className="st1" x1="49.49" y1="43.49" x2="42.96" y2="36.74"/>
            <circle className="st3" cx="15.53" cy="55.36" r="3.91"/>
            <circle className="st2" cx="36.82" cy="28.49" r="10.66"/>
            <circle className="st3" cx="80.47" cy="4.41" r="3.91"/>
        </svg>
    );
};